import { Box, Button, Typography } from "@mui/material";
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayArrowIcon,
} from "@mui/icons-material";
import {
  useCreateBingoSession,
  useGetBingoSessionByBookingId,
  useStartBingoSession,
  useDrawNextNumber,
  useResetBingoSession,
} from "../../../services/stagemode_api/moderator/bingo_api";
import { useCurrentTalentProfile } from "../../../hooks";
import { enqueueSnackbar } from "notistack";
import axios from "axios";
import { useMemo, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../../hooks";

const BingoManagement = ({ bookingId }: { bookingId: string }) => {
  // ---- Style tokens (Netflix-like) ----
  const COLORS = {
    surface: "#141414",
    surfaceAlt: "#1b1b1b",
    border: "rgba(255,255,255,0.12)",
    text: "#ffffff",
    textMuted: "rgba(255,255,255,0.74)",
    red: "#E50914",
    redHover: "#b20710",
    green: "#16a34a",
    greenHover: "#12823c",
    grayBtnBg: "#2a2a2a",
    grayBtnHover: "#323232",
  } as const;

  const surfaceSx = {
    p: { xs: 2, sm: 2.25 },
    borderRadius: 2,
    bgcolor: COLORS.surface,
    border: "1px solid",
    borderColor: COLORS.border,
    color: COLORS.text,
  } as const;

  const labelSx = {
    fontWeight: 800,
    letterSpacing: "-0.01em",
  } as const;

  const pillBase = {
    px: 1.25,
    py: 0.5,
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    border: "1px solid",
    display: "inline-block",
  } as const;

  const redCta = {
    borderRadius: 2,
    fontWeight: 800,
    textTransform: "none",
    bgcolor: COLORS.red,
    "&:hover": { bgcolor: COLORS.redHover },
  } as const;

  const outlineGray = {
    borderRadius: 2,
    fontWeight: 800,
    textTransform: "none",
    bgcolor: COLORS.grayBtnBg,
    border: "1px solid",
    borderColor: COLORS.border,
    "&:hover": {
      bgcolor: COLORS.grayBtnHover,
      borderColor: COLORS.border,
    },
  } as const;

  // ---- Profile / Auth / Role ----
  const { currentProfile } = useCurrentTalentProfile();
  const profileHostId = Number(currentProfile?.id) || undefined;

  const { userInfo } = useAuth();
  const currentUserId = userInfo?.id != null ? Number(userInfo.id) : undefined;

  const location = useLocation();
  const isModeratorRoute = location.pathname.startsWith("/moderator");

  // Single source of truth for host id used in API calls
  const effectiveHostId = useMemo(() => {
    return isModeratorRoute ? currentUserId : profileHostId;
  }, [isModeratorRoute, currentUserId, profileHostId]);

  const [lastDrawnNumber, setLastDrawnNumber] = useState<number | null>(null);

  // Number Generator Modal state
  const [numberModalOpen, setNumberModalOpen] = useState(false);
  const [numberModalValue, setNumberModalValue] = useState<number | null>(null);

  const { update: createBingoSession, isLoading: createLoading } =
    useCreateBingoSession({ key: ["bingo", "create", bookingId] });

  const {
    data: bingoSessionData,
    refetch: refetchBingoSession,
    isLoading: bingoSessionLoading,
  } = useGetBingoSessionByBookingId({
    options: { enabled: !!bookingId, refetchInterval: 1500, refetchOnWindowFocus: true },
    params: { bookingId },
    key: ["bingo", "session", bookingId],
  });

  const { sessionId, status } = useMemo(() => {
    const sid = (bingoSessionData?.data?.session_id) as string | number;
    const sRaw = bingoSessionData?.data?.status;
    const hasStatus = typeof sRaw === "string" && sRaw.trim().length > 0;
    const s = hasStatus ? String(sRaw).toLowerCase() : null; // "waiting" | "active" | "ended" | null
    return { sessionId: String(sid || ""), status: s as "waiting" | "active" | "ended" | null };
  }, [bingoSessionData]);

  // Winner name extraction (robust to different API shapes)
  const winnerName = useMemo(() => {
    const d: any = bingoSessionData?.data ?? {};
    return (
      d?.winner?.display_name ||
      d?.winner?.name ||
      d?.winner?.username ||
      d?.winner_name ||
      d?.winning_user?.name ||
      d?.winners?.[0]?.name ||
      d?.winners?.[0]?.display_name ||
      null
    );
  }, [bingoSessionData]);

  const hasWinner = Boolean(winnerName);

  const hasUsableSession = Boolean(sessionId) && status !== null;
  const isWaiting = status === "waiting";
  const isActive = status === "active";
  const isEnded = status === "ended";

  const { update: startBingoSession, isLoading: startLoading } =
    useStartBingoSession({ key: ["bingo", "start", sessionId], params: { sessionId } });

  const { update: drawNextNumber, isLoading: drawLoading } =
    useDrawNextNumber({ key: ["bingo", "draw", sessionId], params: { sessionId } });

  const { update: resetBingoSession, isLoading: resetLoading } =
    useResetBingoSession({ key: ["bingo", "reset", sessionId], params: { sessionId } });

  // Close number modal on round transitions / winner
  useEffect(() => {
    if (hasWinner || isEnded || isWaiting) {
      setNumberModalOpen(false);
      setNumberModalValue(null);
    }
  }, [hasWinner, isEnded, isWaiting]);

  // ---- Actions (host-id aware) ----
  const guardIds = (needsBooking = true) => {
    const booking_id = Number(bookingId);
    if (needsBooking && !booking_id) {
      enqueueSnackbar("Missing booking id", { variant: "error" });
      return { ok: false as const, booking_id: undefined, host_id: undefined };
    }
    if (!effectiveHostId) {
      // clarify which id is missing based on role
      enqueueSnackbar(
        isModeratorRoute
          ? "Missing moderator user id"
          : "Missing host id (talent profile not loaded)",
        { variant: "error" }
      );
      return { ok: false as const, booking_id, host_id: undefined };
    }
    return { ok: true as const, booking_id, host_id: Number(effectiveHostId) };
  };

  const handleCreateBingoSession = async () => {
    const { ok, booking_id, host_id } = guardIds(true);
    if (!ok || !booking_id || !host_id) return;

    try {
      const res = await createBingoSession({ booking_id, host_id });
      console.log("[Bingo] create →", res);
      await refetchBingoSession();
      enqueueSnackbar("Bingo session created", { variant: "success" });
    } catch (err: any) {
      const status = axios.isAxiosError(err) ? err.response?.status : err?.status;
      const message = axios.isAxiosError(err)
        ? err.response?.data?.message
        : err?.response?.data?.message || err?.message;
      enqueueSnackbar(message || "Failed to create bingo session", { variant: "error" });
      if (status) console.warn("[Bingo] create failed, status:", status);
    }
  };

  const handleDrawNextNumber = async () => {
    if (!hasUsableSession) {
      enqueueSnackbar("No active bingo session", { variant: "error" });
      return;
    }
    if (!isActive) {
      enqueueSnackbar("Start the session to draw numbers", { variant: "warning" });
      return;
    }
    if (hasWinner) {
      enqueueSnackbar("Winner already decided — start a new session to play again.", { variant: "info" });
      return;
    }

    const { ok, host_id } = guardIds(false);
    if (!ok || !host_id) return;

    try {
      const res = await drawNextNumber({ host_id });
      console.log("[Bingo] draw →", res);
      const n = (res as any)?.data?.number ?? (res as any)?.number ?? null;
      if (typeof n === "number") {
        setLastDrawnNumber(n);
        setNumberModalValue(n);
        setNumberModalOpen(true);
        enqueueSnackbar(`Drawn number: ${n}`, { variant: "success" });
      } else {
        enqueueSnackbar("Number drawn (no payload)", { variant: "info" });
      }
      await refetchBingoSession();
    } catch (err: any) {
      const status = axios.isAxiosError(err) ? err.response?.status : err?.status;
      const message = axios.isAxiosError(err)
        ? err.response?.data?.message
        : err?.response?.data?.message || err?.message;
      enqueueSnackbar(message || "Failed to draw next number", { variant: "error" });
      if (status) console.warn("[Bingo] draw failed, status:", status);
    }
  };

  const handleResetBingoSession = async () => {
    if (!hasUsableSession) {
      enqueueSnackbar("No bingo session to reset", { variant: "error" });
      return;
    }
    const { ok, host_id } = guardIds(false);
    if (!ok || !host_id) return;

    try {
      const res = await resetBingoSession({ host_id });
      console.log("[Bingo] reset →", res);
      setLastDrawnNumber(null);
      setNumberModalValue(null);
      await refetchBingoSession();
      enqueueSnackbar("Bingo session reset", { variant: "success" });
    } catch (err: any) {
      const status = axios.isAxiosError(err) ? err.response?.status : err?.status;
      const message = axios.isAxiosError(err)
        ? err.response?.data?.message
        : err?.response?.data?.message || err?.message;
      enqueueSnackbar(message || "Failed to reset bingo session", { variant: "error" });
      if (status) console.warn("[Bingo] reset failed, status:", status);
    }
  };

  // Optional: full “new round” flow that resets, restarts, and recreates a session
  const handleStartNewRound = async () => {
    const { ok, booking_id, host_id } = guardIds(true);
    if (!ok || !booking_id || !host_id) return;

    if (!hasUsableSession) {
      enqueueSnackbar("No bingo session available", { variant: "error" });
      return;
    }
    try {
      await resetBingoSession({ host_id });
      await refetchBingoSession();
      await startBingoSession({});
      setLastDrawnNumber(null);
      setNumberModalValue(null);
      await createBingoSession({ booking_id, host_id }); // ensure fresh session if your backend expects it
      enqueueSnackbar("New Bingo session started", { variant: "success" });
    } catch (err: any) {
      const status = axios.isAxiosError(err) ? err.response?.status : err?.status;
      const message = axios.isAxiosError(err)
        ? err.response?.data?.message
        : err?.response?.data?.message || err?.message;
      enqueueSnackbar(message || "Failed to start a new round", { variant: "error" });
      if (status) console.warn("[Bingo] new round failed, status:", status);
    }
  };

  // ---- UI ----
  if (bingoSessionLoading) {
    return <Typography sx={{ color: COLORS.textMuted }}>…Loading</Typography>;
  }

  // Status pill color
  const statusColorSx =
    hasWinner
      ? { bgcolor: "rgba(22,163,74,0.15)", borderColor: COLORS.green, color: "#d1f7db" }
      : isActive
      ? { bgcolor: "rgba(22,163,74,0.15)", borderColor: COLORS.green, color: "#d1f7db" }
      : isWaiting
      ? { bgcolor: "rgba(255,255,255,0.08)", borderColor: COLORS.border, color: COLORS.textMuted }
      : isEnded
      ? { bgcolor: "rgba(255,255,255,0.06)", borderColor: COLORS.border, color: COLORS.textMuted }
      : null;

  const hasUsableAndActiveNoWinner = hasUsableSession && isActive && !hasWinner;

  return (
    <Box sx={{ width: "100%", color: COLORS.text }}>
      {/* Elegant Number Generator Modal */}
      {numberModalOpen && (
        <Box
          onClick={() => setNumberModalOpen(false)}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1300,
            bgcolor: "rgba(0,0,0,0.6)",
            backdropFilter: "blur(2px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              width: "min(680px, 96vw)",
              borderRadius: 3,
              p: { xs: 2.5, sm: 3.5 },
              bgcolor: COLORS.surface,
              border: "1px solid",
              borderColor: COLORS.border,
              boxShadow: "0 30px 80px rgba(0,0,0,.6)",
              background:
                "radial-gradient(900px 420px at 10% -10%, rgba(229,9,20,0.14), transparent 55%), #141414",
              textAlign: "center",
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 900, letterSpacing: ".2px", mb: 1, color: COLORS.text }}
            >
              Number Generator
            </Typography>

            <Box
              sx={{
                mt: 1,
                mb: 2.5,
                px: 2,
                py: 2,
                borderRadius: 2,
                bgcolor: COLORS.surfaceAlt,
                border: "1px solid",
                borderColor: COLORS.border,
                display: "inline-block",
                minWidth: { xs: 240, sm: 320 },
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 900,
                  letterSpacing: "-0.02em",
                  lineHeight: 1,
                  color: "#90caf9",
                }}
              >
                {numberModalValue ?? "—"}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: COLORS.textMuted }}>
                {hasUsableAndActiveNoWinner
                  ? "Tap Draw Next Number to continue."
                  : hasWinner
                  ? "Winner decided. Start a new session to play again."
                  : isWaiting
                  ? "Session is waiting to start."
                  : isEnded
                  ? "Session ended."
                  : ""}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 1, justifyContent: "center", flexWrap: "wrap" }}>
              {hasUsableAndActiveNoWinner && (
                <Button onClick={handleDrawNextNumber} disabled={drawLoading} variant="contained" sx={redCta}>
                  {drawLoading ? "Drawing..." : "Draw Next Number"}
                </Button>
              )}

              {hasUsableAndActiveNoWinner && (
                <Button
                  onClick={handleResetBingoSession}
                  disabled={resetLoading}
                  variant="contained"
                  sx={outlineGray}
                >
                  {resetLoading ? "Resetting..." : "Reset Bingo Session"}
                </Button>
              )}

              {(hasWinner || isEnded) && hasUsableSession && (
                <Button
                  onClick={handleCreateBingoSession}
                  disabled={startLoading || resetLoading}
                  variant="contained"
                  sx={redCta}
                >
                  {startLoading || resetLoading ? "Preparing..." : "Start New Session"}
                </Button>
              )}

              <Button onClick={() => setNumberModalOpen(false)} variant="contained" sx={outlineGray}>
                Close
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Surface container */}
      <Box sx={surfaceSx}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            mb: 1.5,
          }}
        >
          <Typography variant="h6" sx={labelSx}>
            Bingo Management
          </Typography>

          {hasUsableSession && (
            <Box sx={{ ...pillBase, ...(statusColorSx || {}) }}>
              {hasWinner ? "winner-decided" : status}
            </Box>
          )}
        </Box>

        {/* Winner Panel */}
        {hasWinner && (
          <Box
            sx={{
              mt: 0.5,
              mb: 1.5,
              px: 1.25,
              py: 1,
              borderRadius: 1.5,
              bgcolor: COLORS.surfaceAlt,
              border: "1px solid",
              borderColor: COLORS.green,
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography variant="body2" sx={{ color: "#d1f7db", fontWeight: 800 }}>
              Winner:
            </Typography>
            <Typography variant="body2" sx={{ color: "#d1f7db" }}>
              {winnerName}
            </Typography>
          </Box>
        )}

        {/* Last drawn number */}
        {lastDrawnNumber !== null && (
          <Box
            sx={{
              mt: 1,
              mb: 2,
              px: 1.25,
              py: 1,
              borderRadius: 1.5,
              bgcolor: COLORS.surfaceAlt,
              border: "1px solid",
              borderColor: COLORS.border,
              display: "inline-block",
            }}
          >
            <Typography variant="body2" sx={{ color: "#90caf9" }}>
              Last Drawn: <strong>{lastDrawnNumber}</strong>
            </Typography>
          </Box>
        )}

        {/* Divider line */}
        <Box sx={{ height: 1, bgcolor: COLORS.border, my: 2, mx: -2 }} />

        {/* Actions */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 1.25,
          }}
        >
          {!hasUsableSession ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateBingoSession}
              disabled={createLoading}
              sx={{ ...redCta, gridColumn: { xs: "1 / -1", sm: "span 2" } }}
            >
              {createLoading ? "Creating..." : "Create Bingo Session"}
            </Button>
          ) : (
            <>
              {isWaiting && !hasWinner && (
                <Button
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  onClick={handleCreateBingoSession /* if your backend expects 'create' to (re)open */}
                  disabled={startLoading}
                  sx={{ ...redCta, gridColumn: { xs: "1 / -1", sm: "span 2" } }}
                >
                  {startLoading ? "Starting..." : "Start Bingo Session"}
                </Button>
              )}

              {isActive && !hasWinner && (
                <>
                  <Button variant="contained" onClick={() => setNumberModalOpen(true)} sx={{ ...redCta }}>
                    Open Number Generator
                  </Button>

                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={handleResetBingoSession}
                    disabled={resetLoading}
                    sx={{ ...outlineGray }}
                  >
                    {resetLoading ? "Resetting..." : "Reset Bingo Session"}
                  </Button>
                </>
              )}

              {(hasWinner || isEnded) && (
                <Button
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  onClick={handleCreateBingoSession}
                  disabled={startLoading || resetLoading}
                  sx={{ ...redCta, gridColumn: { xs: "1 / -1", sm: "span 2" } }}
                >
                  {startLoading || resetLoading ? "Preparing..." : "Start New Bingo Session"}
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default BingoManagement;
